import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import {
  adminApi,
  AdminApiError,
  type GlobalConfig,
  type LicenseRecord,
  type MintRequest,
  type MintResult,
} from "../api";
import { CopyButton, Empty, Loading, Modal, timeAgo, useToast } from "../ui";

/* Licenses — the minted-token ledger. A quiet list (no table chrome), a
 * right-aligned mint action, click a row → edit modal. Developer notes live
 * in a small card underneath. */

const TIER_STYLE: Record<string, string> = {
  single: "bg-ink-700 text-fg-mute",
  network: "bg-info/15 text-info",
  unlimited: "bg-rose-500/15 text-rose-300",
};

export function LicensesPage() {
  const toast = useToast();
  const [keys, setKeys] = useState<LicenseRecord[] | null>(null);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<LicenseRecord | null>(null);
  const [minting, setMinting] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await adminApi.list();
      setKeys(r.keys ?? []);
    } catch (e) {
      if (!(e instanceof AdminApiError && e.status === 401)) {
        toast("err", e instanceof AdminApiError ? e.message : "Failed to load the ledger");
      }
      setKeys((k) => k ?? []);
    }
  }, [toast]);
  useEffect(() => {
    reload();
  }, [reload]);

  const rows = useMemo(() => {
    if (!keys) return [];
    const f = filter.trim().toLowerCase();
    if (!f) return keys;
    return keys.filter((k) =>
      [k.key, k.entity, k.tier, k.notes].some((v) => (v || "").toLowerCase().includes(f)),
    );
  }, [keys, filter]);

  return (
    <div className="rise-in">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Licenses</h1>
          <p className="mt-1 text-sm text-fg-mute">
            Every token minted, who it belongs to, and your notes on it.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setMinting(true)}>
          <Plus className="h-4 w-4" /> Mint token
        </button>
      </header>

      <div className="relative mt-8 max-w-sm">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-fg-faint" />
        <input
          className="field !pl-9"
          placeholder="Filter by entity, tier, notes…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="card mt-4 overflow-hidden">
        {keys === null ? (
          <Loading label="Loading ledger…" />
        ) : rows.length === 0 ? (
          <Empty
            title={filter ? "No matches." : "No tokens minted yet."}
            hint={filter ? undefined : "Mint token starts the ledger."}
          />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((k) => (
              <li key={k.key}>
                <button
                  className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition hover:bg-ink-850"
                  onClick={() => setSelected(k)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5">
                      <span className="truncate font-medium">{k.entity || "—"}</span>
                      <span className={`pill ${TIER_STYLE[k.tier] ?? TIER_STYLE.single}`}>
                        {k.tier || "single"}
                      </span>
                      {k._corrupt && <span className="pill bg-err/15 text-err">corrupt</span>}
                    </div>
                    <div className="mt-1 truncate font-mono text-xs text-fg-faint">{k.key}</div>
                  </div>
                  {k.notes && (
                    <span className="hidden max-w-56 truncate text-xs text-fg-mute md:block">
                      {k.notes}
                    </span>
                  )}
                  <span className="shrink-0 text-xs text-fg-faint">{timeAgo(k.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <DevNotes />

      {selected && (
        <EditModal
          record={selected}
          onClose={() => setSelected(null)}
          onSaved={(updated) => {
            setKeys((prev) => (prev ?? []).map((k) => (k.key === updated.key ? updated : k)));
            setSelected(updated);
          }}
          onDeleted={(key) => {
            setKeys((prev) => (prev ?? []).filter((k) => k.key !== key));
            setSelected(null);
          }}
        />
      )}
      {minting && (
        <MintModal
          onClose={() => setMinting(false)}
          onMinted={(stub) => setKeys((prev) => [stub, ...(prev ?? [])])}
        />
      )}
    </div>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────

function EditModal({
  record,
  onClose,
  onSaved,
  onDeleted,
}: {
  record: LicenseRecord;
  onClose: () => void;
  onSaved: (r: LicenseRecord) => void;
  onDeleted: (key: string) => void;
}) {
  const toast = useToast();
  const [notes, setNotes] = useState(record.notes || "");
  const [busy, setBusy] = useState(false);

  return (
    <Modal title={record.entity || "License"} onClose={onClose}>
      <div className="space-y-4">
        <div className="rounded-(--radius-field) bg-ink-900 p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="min-w-0 truncate font-mono text-xs text-fg-mute">{record.key}</span>
            <CopyButton text={record.key} />
          </div>
          <div className="mt-2 flex gap-4 text-xs text-fg-faint">
            <span>
              tier <span className="text-fg-mute">{record.tier || "single"}</span>
            </span>
            <span>
              minted <span className="text-fg-mute">{timeAgo(record.created_at)}</span>
            </span>
          </div>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-fg-mute">Notes</span>
          <textarea
            className="field mt-1.5 min-h-24 resize-y"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Who bought it, renewal date, anything future-you needs…"
          />
        </label>

        <div className="flex items-center justify-between">
          <button
            className="btn-danger !px-2.5"
            disabled={busy}
            onClick={async () => {
              if (!confirm(`Delete the ledger record for "${record.entity}"? The token itself stays valid — offline keys can't be revoked.`)) return;
              setBusy(true);
              try {
                await adminApi.remove(record.key);
                toast("ok", "Record deleted");
                onDeleted(record.key);
              } catch (e) {
                toast("err", e instanceof AdminApiError ? e.message : "Delete failed");
                setBusy(false);
              }
            }}
          >
            Delete record
          </button>
          <button
            className="btn-primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const updated = await adminApi.update(record.key, { notes });
                toast("ok", "Saved");
                onSaved(updated);
              } catch (e) {
                toast("err", e instanceof AdminApiError ? e.message : "Save failed");
              } finally {
                setBusy(false);
              }
            }}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Mint modal ────────────────────────────────────────────────────────

const PRESETS = [
  { id: "customer", label: "Customer", hint: "Perpetual", days: null as number | null },
  { id: "annual", label: "Annual", hint: "365 days", days: 365 },
  { id: "beta", label: "Beta", hint: "30 days", days: 30 },
  { id: "trial", label: "Trial", hint: "7 days", days: 7 },
];
const TIERS: MintRequest["tier"][] = ["single", "network", "unlimited"];

function MintModal({
  onClose,
  onMinted,
}: {
  onClose: () => void;
  onMinted: (stub: LicenseRecord) => void;
}) {
  const toast = useToast();
  const [preset, setPreset] = useState("customer");
  const [entity, setEntity] = useState("");
  const [tier, setTier] = useState<MintRequest["tier"]>("single");
  const [maxMachines, setMaxMachines] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MintResult | null>(null);

  const mint = async () => {
    setBusy(true);
    try {
      const days = PRESETS.find((p) => p.id === preset)?.days ?? null;
      const expiryEpochSec = days ? Math.floor(Date.now() / 1000) + days * 86_400 : 0;
      const r = await adminApi.mint({ entity: entity.trim(), tier, maxMachines, expiryEpochSec });
      setResult(r);
      onMinted({ key: r.token, entity: r.entity, tier: r.tier, notes: "", created_at: Date.now() });
      toast("ok", `Token minted for ${r.entity}`);
    } catch (e) {
      toast("err", e instanceof AdminApiError ? e.message : "Mint failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={result ? "Token minted" : "Mint a token"} onClose={onClose}>
      {result ? (
        <div className="space-y-4">
          <p className="text-sm text-fg-mute">
            Copy it now — the private half is never stored, so this is the only time the full
            token is shown.
          </p>
          <div className="rounded-(--radius-field) bg-ink-900 p-3 font-mono text-xs break-all text-fg">
            {result.token}
          </div>
          <div className="flex justify-end gap-2">
            <CopyButton text={result.token} label="Copy token" />
            <button className="btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs font-medium text-fg-mute">Entity</span>
            <input
              className="field mt-1.5"
              placeholder="Server / buyer name"
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              autoFocus
            />
          </label>

          <div>
            <span className="text-xs font-medium text-fg-mute">Duration</span>
            <div className="mt-1.5 grid grid-cols-4 gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPreset(p.id)}
                  className={`rounded-(--radius-field) px-2 py-2 text-center transition ${
                    preset === p.id ? "bg-ink-700 ring-1 ring-rose-500/50" : "bg-ink-900 hover:bg-ink-850"
                  }`}
                >
                  <div className="text-[13px] font-medium">{p.label}</div>
                  <div className="text-[11px] text-fg-faint">{p.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-fg-mute">Tier</span>
              <select
                className="field mt-1.5"
                value={tier}
                onChange={(e) => setTier(e.target.value as MintRequest["tier"])}
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-fg-mute">Max machines</span>
              <input
                className="field mt-1.5"
                type="number"
                min={1}
                value={maxMachines}
                onChange={(e) => setMaxMachines(Math.max(1, Number(e.target.value) || 1))}
              />
            </label>
          </div>

          <div className="flex justify-end">
            <button className="btn-primary" disabled={busy || !entity.trim()} onClick={mint}>
              {busy ? "Minting…" : "Mint"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Developer notes ───────────────────────────────────────────────────

function DevNotes() {
  const toast = useToast();
  const [cfg, setCfg] = useState<GlobalConfig | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .getGlobal()
      .then(setCfg)
      .catch(() => setCfg({ notes: "" }));
  }, []);

  if (!cfg) return null;
  return (
    <div className="card mt-6 p-5">
      <div className="text-sm font-semibold">Developer notes</div>
      <textarea
        className="field mt-2.5 min-h-20 resize-y text-[13px]"
        placeholder="Pricing reminders, upcoming renewals, anything…"
        value={cfg.notes}
        onChange={(e) => setCfg({ ...cfg, notes: e.target.value })}
      />
      <div className="mt-2.5 flex items-center justify-between">
        <span className="text-xs text-fg-faint">
          {cfg.updated_at ? `Saved ${timeAgo(cfg.updated_at)}` : ""}
        </span>
        <button
          className="btn-quiet"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              setCfg(await adminApi.putGlobal(cfg));
              toast("ok", "Notes saved");
            } catch (e) {
              toast("err", e instanceof AdminApiError ? e.message : "Save failed");
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? "Saving…" : "Save notes"}
        </button>
      </div>
    </div>
  );
}
